import React, { KeyboardEvent, useCallback, useState } from 'react';
import { Box, styled, TextareaAutosize, Typography } from '@mui/material';
import { BaseModal } from '@tymely/components';
import { blue, grey } from '@mui/material/colors';
import { usePolicyEditor } from '@tymely/pages/GraphPolicyEditor/components/PolicyEditorProvider';
import { IPolicyMetadata } from '@tymely/atoms';

const UseCaseTextarea = styled(TextareaAutosize)(
    () => `
    box-sizing: border-box;
    width: 100%;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    padding: 8px 12px;
    border-radius: 8px;
    color: ${grey[900]};
    background: ${'#fff'};
    border: 1px solid ${grey[200]};
    box-shadow: 0 2px 2px ${grey[50]};

    &:hover {
      border-color: ${blue[400]};
    }

    &:focus {
      border-color: ${blue[400]};
      box-shadow: 0 0 0 3px ${blue[200]};
    }
  `,
);

type UseCaseModalProps = {
    open: boolean;
    onClose: () => void;
    loading?: boolean;
    genericDescription?: string;
};

export const UseCaseModal = ({ open, onClose, loading, genericDescription }: UseCaseModalProps) => {
    const { policyMetadata, setPolicyMetadata } = usePolicyEditor();
    const [useCase, setUseCase] = useState(policyMetadata?.use_case);

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => e.stopPropagation(), []);

    return (
        <BaseModal
            open={open}
            okLabel="Save"
            onClose={onClose}
            title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Use When
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                        What use-case does this policy solve for this organization? (overrides generic description)
                    </Typography>
                </Box>
            }
            maxWidth="md"
            onOk={() => setPolicyMetadata((prev) => ({ ...prev, use_case: useCase }) as IPolicyMetadata)}
            closable
            loading={loading}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <UseCaseTextarea
                    placeholder="Enter the use case description here"
                    value={useCase}
                    onChange={(e) => setUseCase(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                {genericDescription && (
                    <Box
                        sx={{
                            pt: 1.5,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Generic description:
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            {genericDescription}
                        </Typography>
                    </Box>
                )}
            </Box>
        </BaseModal>
    );
};
